import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, PackageMinus, PackagePlus, History, Search, AlertTriangle, FileUp, Loader2 } from "lucide-react";
import { CRM_API, errMsg } from "./api";
import PageHeader from "./components/PageHeader";
import { useCrmAuth } from "./CrmAuth";

const CATEGORIES = [
  { k: "chemical", l: "Chemicals" },
  { k: "ppf", l: "PPF Film" },
  { k: "tint", l: "Tint Film" },
  { k: "coating", l: "Coatings" },
  { k: "consumable", l: "Consumables" },
  { k: "tool", l: "Tools" },
  { k: "other", l: "Other" },
];
const UNITS = ["pcs", "ml", "l", "ft", "m", "roll", "box", "kit", "set"];
const REASONS = [
  { k: "restock", l: "Restock (purchase)" },
  { k: "usage", l: "Usage (job)" },
  { k: "damage", l: "Damaged / wasted" },
  { k: "return", l: "Returned to supplier" },
  { k: "correction", l: "Inventory correction" },
];
const catLabel = (k) => CATEGORIES.find((c) => c.k === k)?.l || k;
const inp = "w-full bg-white/5 border border-white/12 rounded-lg px-3 py-2 outline-none focus:border-[#1F5EFF] text-white text-sm";

const STATUS = {
  ok: { l: "In stock", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" },
  low: { l: "Low", cls: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  out: { l: "Out", cls: "text-red-400 bg-red-500/10 border-red-500/20" },
};

export default function Inventory() {
  const { user } = useCrmAuth();
  const canManage = user?.role === "owner" || user?.role === "manager";
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);   // item object
  const [adjusting, setAdjusting] = useState(null); // item object
  const [history, setHistory] = useState(null);   // {item, transactions}
  const [importing, setImporting] = useState(false);

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (cat) params.category = cat;
    if (lowOnly) params.low = 1;
    CRM_API.get("/inventory/items", { params }).then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));
    CRM_API.get("/inventory/summary").then(({ data }) => setSummary(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [q, cat, lowOnly]); // eslint-disable-line

  const grouped = useMemo(() => {
    const map = {};
    for (const c of CATEGORIES) map[c.k] = [];
    for (const r of rows) (map[r.category] ||= []).push(r);
    return map;
  }, [rows]);

  const remove = async (it) => {
    if (!confirm(`Delete "${it.name}" and its history?`)) return;
    try { await CRM_API.delete(`/inventory/items/${it.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const openHistory = async (it) => {
    try {
      const { data } = await CRM_API.get(`/inventory/items/${it.id}`);
      setHistory(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="crm-inventory-page">
      <PageHeader
        eyebrow="Studio"
        title="Inventory"
        subtitle="Chemicals, PPF & tint film stock. Adjust on every job — low items get flagged automatically."
        right={(
          <div className="flex items-center gap-2">
            {canManage && (
              <button onClick={() => setImporting(true)} data-testid="btn-import-pdf"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white/8 hover:bg-white/15 text-white font-medium transition-colors">
                <FileUp className="w-4 h-4" /> Import supplier PDF
              </button>
            )}
            <button onClick={() => setAdding(true)} data-testid="btn-add-item"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium transition-colors">
              <Plus className="w-4 h-4" /> New item
            </button>
          </div>
        )}
      />

      {/* Summary */}
      {summary && (
        <div className="px-6 md:px-10 pt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { l: "ITEMS", v: summary.items, tid: "sum-items" },
            { l: "LOW STOCK", v: summary.low, tid: "sum-low", warn: summary.low > 0 },
            { l: "OUT OF STOCK", v: summary.out, tid: "sum-out", danger: summary.out > 0 },
            { l: "STOCK VALUE", v: `$${summary.stock_value.toLocaleString()}`, tid: "sum-value" },
          ].map((s) => (
            <div key={s.l} data-testid={s.tid}
              className={`p-4 rounded-xl border ${s.danger ? "bg-red-500/5 border-red-500/20" : s.warn ? "bg-amber-500/5 border-amber-500/20" : "bg-white/5 border-white/10"}`}>
              <div className="font-mono-alt text-[9px] tracking-widest text-white/40 uppercase">{s.l}</div>
              <div className="font-display text-2xl text-white mt-1 tabular-nums">{s.v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="px-6 md:px-10 pt-6 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, brand, SKU…"
            className={`${inp} pl-8 w-56`} data-testid="inv-search" />
        </div>
        <button onClick={() => setCat("")} data-testid="inv-cat-all"
          className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${!cat ? "bg-[#1F5EFF] text-white" : "bg-white/5 text-white/60 hover:text-white"}`}>
          All
        </button>
        {CATEGORIES.map((c) => (
          <button key={c.k} onClick={() => setCat(c.k)} data-testid={`inv-cat-${c.k}`}
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${cat === c.k ? "bg-[#1F5EFF] text-white" : "bg-white/5 text-white/60 hover:text-white"}`}>
            {c.l}
          </button>
        ))}
        <button onClick={() => setLowOnly((v) => !v)} data-testid="inv-low-toggle"
          className={`px-3 py-2 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${lowOnly ? "bg-amber-500 text-black" : "bg-white/5 text-white/60 hover:text-white"}`}>
          <AlertTriangle className="w-3.5 h-3.5" /> Low stock
        </button>
      </div>

      {/* Items */}
      <div className="px-6 md:px-10 py-8 space-y-10">
        {adding && <ItemForm onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
        {editing && <ItemForm initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        {rows.length === 0 && !adding ? (
          <div className="text-center py-20 text-white/30" data-testid="inv-empty">
            <Boxes_ />No items yet. Add your first chemical or film roll above.
          </div>
        ) : (
          CATEGORIES.filter((c) => grouped[c.k]?.length).map((c) => (
            <section key={c.k}>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-6 h-px bg-[#1F5EFF]" />
                <span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">{c.l}</span>
                <span className="text-white/30 text-xs">· {grouped[c.k].length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {grouped[c.k].map((it) => {
                  const st = STATUS[it.status];
                  return (
                    <div key={it.id} data-testid={`inv-item-${it.id}`}
                      className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="min-w-0">
                          <div className="text-white font-medium text-[15px] leading-snug truncate">{it.name}</div>
                          <div className="text-white/40 text-[11px] mt-0.5">
                            {[it.brand, it.sku && `SKU ${it.sku}`, it.supplier].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full border text-[10px] font-mono-alt uppercase tracking-wider whitespace-nowrap ${st.cls}`}
                          data-testid={`inv-status-${it.id}`}>{st.l}</span>
                      </div>
                      <div className="flex items-end justify-between mt-3">
                        <div>
                          <span className="font-display text-3xl text-white tabular-nums" data-testid={`inv-qty-${it.id}`}>
                            {Number(it.quantity).toLocaleString()}
                          </span>
                          <span className="text-white/40 text-xs ml-1.5">{it.unit}</span>
                        </div>
                        <div className="text-right text-[10px] font-mono-alt uppercase tracking-widest text-white/35">
                          <div>MIN {it.min_quantity} {it.unit}</div>
                          {it.cost != null && <div>${it.cost}/{it.unit}</div>}
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-end gap-1">
                        <button onClick={() => setAdjusting(it)} data-testid={`inv-adjust-${it.id}`}
                          className="text-[11px] px-2 py-1.5 rounded text-[#8db2ff] hover:text-white hover:bg-white/5 inline-flex items-center gap-1">
                          <PackagePlus className="w-3.5 h-3.5" /> Adjust
                        </button>
                        <button onClick={() => openHistory(it)} data-testid={`inv-history-${it.id}`}
                          className="p-1.5 rounded text-white/40 hover:text-white hover:bg-white/5" title="History">
                          <History className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setEditing(it)} data-testid={`inv-edit-${it.id}`}
                          className="text-white/40 hover:text-white text-[11px] px-2 py-1">Edit</button>
                        {canManage && (
                          <button onClick={() => remove(it)} data-testid={`inv-delete-${it.id}`}
                            className="p-1.5 rounded text-white/40 hover:text-red-400 hover:bg-red-500/10">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>

      {importing && <ImportPdfModal onClose={() => setImporting(false)} onDone={() => { setImporting(false); load(); }} />}
      {adjusting && (
        <AdjustModal item={adjusting} onClose={() => setAdjusting(null)}
          onDone={() => { setAdjusting(null); load(); }} />
      )}
      {history && <HistoryModal data={history} onClose={() => setHistory(null)} />}
    </div>
  );
}

const Boxes_ = () => (
  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
    <PackageMinus className="w-5 h-5 text-white/30" />
  </div>
);

/* ================= Supplier PDF import ================= */
function ImportPdfModal({ onClose, onDone }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState(null); // {supplier, reference, date, items, warnings, invoice_subtotal, parsed_total}
  const [supplier, setSupplier] = useState("");
  const [reference, setReference] = useState("");

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await CRM_API.post("/inventory/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParsed(data);
      setSupplier(data.supplier || "");
      setReference(data.reference || "");
      if (!data.items.length) toast.warning("No line items found in this PDF");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const setItem = (i, k, v) => setParsed((p) => ({ ...p, items: p.items.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await CRM_API.post("/inventory/import/apply", {
        supplier: supplier || null,
        reference: reference || null,
        items: parsed.items.map((i) => ({
          name: i.name, sku: i.sku || null, qty: Number(i.qty) || 0,
          unit_price: i.unit_price != null ? Number(i.unit_price) : null,
          include: !!i.include, category: i.category || "other",
        })),
      });
      toast.success(`Imported: ${data.created} new, ${data.updated} restocked${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const included = parsed ? parsed.items.filter((i) => i.include) : [];

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="inv-import-modal">
      <div className="w-full max-w-4xl rounded-2xl bg-[#121216] border border-white/12 max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-5">
          <span className="w-6 h-px bg-[#1F5EFF]" />
          <span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">Import supplier order (PDF)</span>
        </div>

        {!parsed ? (
          <div className="p-10 text-center border-2 border-dashed border-white/12 rounded-xl">
            {busy ? (
              <Loader2 className="w-6 h-6 animate-spin text-[#1F5EFF] mx-auto" />
            ) : (
              <>
                <FileUp className="w-8 h-8 text-white/30 mx-auto mb-3" />
                <div className="text-white/70 text-sm mb-1">Upload the PDF your supplier sent you</div>
                <div className="text-white/35 text-xs mb-5">Receipt / invoice with an items table (e.g. CARZILLA) — line items are parsed automatically</div>
                <button onClick={() => fileRef.current?.click()} data-testid="inv-import-choose"
                  className="px-6 py-2.5 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white text-sm font-medium transition-colors">
                  Choose PDF
                </button>
                <input ref={fileRef} type="file" accept="application/pdf" className="hidden" data-testid="inv-import-file"
                  onChange={(e) => upload(e.target.files?.[0])} />
              </>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
              <label className="block">
                <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest mb-1">Supplier</div>
                <input value={supplier} onChange={(e) => setSupplier(e.target.value)} className={inp} data-testid="inv-import-supplier" />
              </label>
              <label className="block">
                <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest mb-1">Order / invoice #</div>
                <input value={reference} onChange={(e) => setReference(e.target.value)} className={inp} data-testid="inv-import-reference" />
              </label>
              <div className="flex items-end pb-1 text-white/40 text-xs">
                {parsed.date || ""} · {parsed.items.length} lines parsed
                {parsed.parsed_total != null && ` · $${parsed.parsed_total.toLocaleString("en-CA", { minimumFractionDigits: 2 })}`}
              </div>
            </div>

            {parsed.warnings?.length > 0 && (
              <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs space-y-1" data-testid="inv-import-warnings">
                {parsed.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />{w}</div>
                ))}
              </div>
            )}

            <div className="max-h-[42vh] overflow-y-auto space-y-1 mb-4 pr-1" data-testid="inv-import-items">
              {parsed.items.map((it, i) => (
                <div key={i} className={`flex items-center gap-2 p-2.5 rounded-lg border text-sm ${it.include ? "bg-white/5 border-white/10" : "bg-transparent border-white/5 opacity-40"}`}
                  data-testid={`inv-import-row-${i}`}>
                  <input type="checkbox" checked={!!it.include} onChange={(e) => setItem(i, "include", e.target.checked)}
                    className="accent-[#1F5EFF] w-4 h-4 flex-shrink-0" data-testid={`inv-import-include-${i}`} />
                  <div className="flex-1 min-w-0">
                    <input value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} className="w-full bg-transparent outline-none text-white text-sm" />
                    <div className="flex items-center gap-2 text-[10px] font-mono-alt">
                      {it.sku && <span className="text-white/35">SKU {it.sku}</span>}
                      {it.matched_item_id
                        ? <span className="text-blue-300">↳ restocks “{it.matched_name}”</span>
                        : <span className="text-emerald-300">↳ new item</span>}
                      {it.review && <span className="text-amber-300">· verify row</span>}
                    </div>
                  </div>
                  <input type="number" min="0" step="1" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)}
                    className={`${inp} !w-16 !py-1 text-center`} title="Qty" />
                  <input type="number" min="0" step="0.01" value={it.unit_price ?? ""} onChange={(e) => setItem(i, "unit_price", e.target.value)}
                    className={`${inp} !w-24 !py-1 text-right`} title="Unit cost" placeholder="cost" />
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button onClick={apply} disabled={busy || !included.length} data-testid="inv-import-apply"
                className="flex-1 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white text-sm font-medium transition-colors disabled:opacity-40">
                {busy ? "…" : `Import ${included.length} item${included.length === 1 ? "" : "s"} into stock`}
              </button>
              <button onClick={() => setParsed(null)} className="px-4 py-3 text-white/50 text-sm hover:text-white">Back</button>
              <button onClick={onClose} className="px-4 py-3 text-white/50 text-sm hover:text-white" data-testid="inv-import-cancel">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ItemForm({ initial, onCancel, onSaved }) {
  const [f, setF] = useState({
    name: initial?.name || "",
    category: initial?.category || "chemical",
    brand: initial?.brand || "",
    sku: initial?.sku || "",
    unit: initial?.unit || "pcs",
    quantity: initial?.quantity ?? 0,
    min_quantity: initial?.min_quantity ?? 0,
    cost: initial?.cost ?? "",
    supplier: initial?.supplier || "",
    location: initial?.location || "",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        quantity: Number(f.quantity) || 0,
        min_quantity: Number(f.min_quantity) || 0,
        cost: f.cost === "" ? null : Number(f.cost),
      };
      if (initial) {
        const { quantity, ...rest } = payload; // qty changes only via Adjust
        await CRM_API.patch(`/inventory/items/${initial.id}`, rest);
      } else {
        await CRM_API.post("/inventory/items", payload);
      }
      toast.success(initial ? "Updated" : "Added");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-5 rounded-xl bg-[#1F5EFF]/5 border border-[#1F5EFF]/25 max-w-2xl space-y-2" data-testid="inv-item-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Name (e.g. PPF film roll 60in)" className={inp} data-testid="inv-form-name" />
        <select value={f.category} onChange={(e) => set("category", e.target.value)} className={inp} data-testid="inv-form-category">
          {CATEGORIES.map((c) => <option key={c.k} value={c.k}>{c.l}</option>)}
        </select>
        <input value={f.brand} onChange={(e) => set("brand", e.target.value)} placeholder="Brand" className={inp} data-testid="inv-form-brand" />
        <input value={f.sku} onChange={(e) => set("sku", e.target.value)} placeholder="SKU" className={inp} data-testid="inv-form-sku" />
        <select value={f.unit} onChange={(e) => set("unit", e.target.value)} className={inp} data-testid="inv-form-unit">
          {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        {!initial && (
          <input type="number" value={f.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder="Initial quantity" className={inp} data-testid="inv-form-quantity" />
        )}
        <input type="number" value={f.min_quantity} onChange={(e) => set("min_quantity", e.target.value)} placeholder="Min quantity (low alert)" className={inp} data-testid="inv-form-min" />
        <input type="number" value={f.cost} onChange={(e) => set("cost", e.target.value)} placeholder="Cost per unit, CAD" className={inp} data-testid="inv-form-cost" />
        <input value={f.supplier} onChange={(e) => set("supplier", e.target.value)} placeholder="Supplier" className={inp} data-testid="inv-form-supplier" />
        <input value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="Shelf / location" className={inp} data-testid="inv-form-location" />
      </div>
      <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notes" rows={2} className={inp} data-testid="inv-form-notes" />
      <div className="flex items-center gap-2 pt-1">
        <button onClick={save} disabled={busy} className="px-6 py-2 rounded-lg bg-[#1F5EFF] text-white text-sm font-medium hover:bg-white hover:text-black disabled:opacity-50" data-testid="inv-form-save">
          {busy ? "…" : initial ? "Save changes" : "Add item"}
        </button>
        <button onClick={onCancel} className="px-3 py-2 text-white/50 text-sm hover:text-white" data-testid="inv-form-cancel">Cancel</button>
      </div>
    </div>
  );
}

function AdjustModal({ item, onClose, onDone }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("usage");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (sign) => {
    const v = Number(delta);
    if (!v || v <= 0) { toast.error("Enter a positive amount"); return; }
    setBusy(true);
    try {
      await CRM_API.post(`/inventory/items/${item.id}/adjust`, { delta: sign * v, reason, note });
      toast.success(`${sign > 0 ? "+" : "−"}${v} ${item.unit} — ${catLabel(item.category)}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="inv-adjust-modal">
      <div className="w-full max-w-md rounded-2xl bg-[#121216] border border-white/12 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="text-white font-medium text-lg">{item.name}</div>
        <div className="text-white/40 text-xs mt-1 mb-5">Current stock: {item.quantity} {item.unit}</div>
        <div className="space-y-2">
          <input type="number" min="0" step="any" value={delta} onChange={(e) => setDelta(e.target.value)}
            placeholder={`Amount in ${item.unit}`} className={inp} data-testid="inv-adjust-amount" autoFocus />
          <select value={reason} onChange={(e) => setReason(e.target.value)} className={inp} data-testid="inv-adjust-reason">
            {REASONS.map((r) => <option key={r.k} value={r.k}>{r.l}</option>)}
          </select>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (job, PO #…)" className={inp} data-testid="inv-adjust-note" />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button onClick={() => submit(-1)} disabled={busy} data-testid="inv-adjust-minus"
            className="py-2.5 rounded-lg bg-red-500/15 border border-red-500/30 text-red-300 text-sm font-medium hover:bg-red-500/25 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <PackageMinus className="w-4 h-4" /> Deduct
          </button>
          <button onClick={() => submit(1)} disabled={busy} data-testid="inv-adjust-plus"
            className="py-2.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-sm font-medium hover:bg-emerald-500/25 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <PackagePlus className="w-4 h-4" /> Add
          </button>
        </div>
        <button onClick={onClose} className="w-full mt-2 py-2 text-white/50 text-sm hover:text-white" data-testid="inv-adjust-cancel">Cancel</button>
      </div>
    </div>
  );
}

function HistoryModal({ data, onClose }) {
  const { item, transactions } = data;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="inv-history-modal">
      <div className="w-full max-w-lg rounded-2xl bg-[#121216] border border-white/12 p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="text-white font-medium text-lg mb-1">{item.name}</div>
        <div className="text-white/40 text-xs mb-5">Stock movement history</div>
        {transactions.length === 0 ? (
          <div className="text-white/30 text-sm italic">No movements yet.</div>
        ) : (
          <div className="space-y-2">
            {transactions.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 text-sm">
                <div>
                  <span className={`font-mono-alt tabular-nums ${t.delta > 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {t.delta > 0 ? "+" : ""}{t.delta} {item.unit}
                  </span>
                  <span className="text-white/50 ml-3">{REASONS.find((r) => r.k === t.reason)?.l || t.reason}</span>
                  {t.note && <div className="text-white/35 text-xs mt-0.5">{t.note}</div>}
                </div>
                <div className="text-right text-[10px] text-white/30 font-mono-alt whitespace-nowrap">
                  <div>{new Date(t.created_at).toLocaleDateString("en-CA")}</div>
                  <div>{t.employee_email || ""}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <button onClick={onClose} className="w-full mt-4 py-2 text-white/50 text-sm hover:text-white" data-testid="inv-history-close">Close</button>
      </div>
    </div>
  );
}
